import { memo, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Breadcrumbs as MUIBreadcrumbs, Link as MLink, Typography, styled } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useBreadcrumbs } from 'contexts/BreadcrumbsProvider';
import { useBreadcrumbsBrowserNavigation } from 'utils/hooks';
import { Home } from '@mui/icons-material';

const HomeButton = styled(Home)({
  cursor: 'pointer',
  marginBottom: '4px',
});

const normalizePath = (p: string) => p.replace(/\/+$/, '') || '/';

export const AppBreadcrumbs = memo(() => {
  useBreadcrumbsBrowserNavigation();
  const { items, remove, baseLength, removeAllExtras, pushRemovedByIds, setClickedBreadcrumb } = useBreadcrumbs();
  const navigate = useNavigate();
  const location = useLocation();

  const handleBreadcrumbClick = useCallback(
    (clickedIndex: number, path: string) => {
      const extrasEnd = items.length - 1;

      setClickedBreadcrumb(true);

      if (clickedIndex < baseLength || extrasEnd < baseLength) {
        removeAllExtras();
      } else {
        const idsToRemove: string[] = [];
        // Here we're using length based (indexed from 1) extrasEnd as an array index, thus another -1
        for (let i = extrasEnd - 1; i >= clickedIndex; i--) {
          idsToRemove.push(items[i].id);
        }
        remove(idsToRemove);
        pushRemovedByIds(idsToRemove);
      }
      navigate(path);
    },
    [baseLength, items, setClickedBreadcrumb, navigate, removeAllExtras, remove, pushRemovedByIds]
  );

  const currentPath = useMemo(() => normalizePath(location.pathname), [location.pathname]);

  if (!items.length) return null;

  const last = items[items.length - 1];
  const lastText = typeof last.label === 'string' ? last.label : undefined;

  return (
    <>
      {lastText && (
        <Helmet>
          <title>{lastText} | Flowio</title>
        </Helmet>
      )}
      <MUIBreadcrumbs aria-label="breadcrumb" sx={{ height: 36, display: 'flex', alignItems: 'center', gap: 1 }}>
        <MLink
          data-testid="app-breadcrumb-home"
          onClick={() => {
            removeAllExtras();
            navigate('/');
          }}>
          <HomeButton fontSize="small" />
        </MLink>
        {items
          .filter((it) => !!it.label)
          .map((it, idx) => {
            const isCurrent = normalizePath(it.path) === currentPath;

            return !isCurrent ? (
              <MLink
                key={it.id}
                data-testid={`app-breadcrumb-item-${idx}`}
                onClick={() => handleBreadcrumbClick(idx, it.path)}
                underline="hover"
                sx={{ cursor: 'pointer' }}>
                {it.label}
              </MLink>
            ) : (
              <Typography key={it.id} component="span" color="text.primary" data-testid={`app-breadcrumb-current-${idx}`}>
                {it.label}
              </Typography>
            );
          })}
      </MUIBreadcrumbs>
    </>
  );
});
